import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { devFilesApi } from '../../services/api';
import { formatDateOnly } from '../../utils/dateTime';

// Picks rev log entries for a release email (Send Email to Users, Release template). The ticked
// entries' change lines become the message's bullets; nothing is written anywhere. Portalled so it
// stacks above the email popup that opened it.

const SHOWN = 60;

const RevisionPickerModal = ({ onClose, onApply }) => {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    devFilesApi.getRevLog()
      .then(res => setRevisions((res.data?.revisions || []).slice(0, SHOWN)))
      .catch(() => setError('Failed to load the rev log'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Rev log order is newest first; keeping it means the first ticked entry is the newest version.
  const apply = () => {
    onApply(revisions.filter(r => selected.has(r.id)));
    onClose();
  };

  return createPortal(
    <div className="stui-modal-overlay stui-modal-overlay-nested">
      <div className="stui-modal stui-modal-xl">
        <div className="stui-modal-header">
          <h3>Pick Revisions</h3>
          <button className="stui-modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="stui-modal-body stui-modal-body-grid">
          {loading ? (
            <div className="stui-text-empty">Loading...</div>
          ) : error ? (
            <div className="stui-text-error">{error}</div>
          ) : (
            <div className="stui-data-grid-container">
              <div className="stui-data-grid-scroll-area">
                <table className="stui-data-grid stui-data-grid-auto">
                  <thead>
                    <tr>
                      <th className="stui-td-center"></th>
                      <th>Version</th>
                      <th>Date</th>
                      <th>Summary</th>
                      <th>Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revisions.map(r => (
                      <tr key={r.id} onClick={() => toggle(r.id)}>
                        <td className="stui-td-center">
                          <input type="checkbox" className="stui-checkbox-input" checked={selected.has(r.id)} onChange={() => toggle(r.id)} onClick={e => e.stopPropagation()} />
                        </td>
                        <td className="stui-text-nowrap">{r.version}</td>
                        <td className="stui-text-nowrap">{formatDateOnly(r.date)}</td>
                        <td>{r.summary}</td>
                        <td className="stui-td-center">{(r.changes || []).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="stui-modal-footer stui-modal-footer-split">
          <span className="stui-text-sm stui-td-dim">
            {`${selected.size} selected · their change lines become the bullets; edit them for users before sending`}
          </span>
          <div className="stui-row-8">
            <button className="stui-btn stui-btn-secondary" onClick={onClose}>Cancel</button>
            <button className="stui-btn stui-btn-primary" onClick={apply} disabled={selected.size === 0}>Insert</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RevisionPickerModal;
